import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot, Send, ExternalLink, Loader2, ArrowRight, AlertTriangle } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import {
  Container, Section, SectionHeading, Breadcrumbs, Card, Badge, Alert,
} from '../../components/portal/ui'
import {
  askAssistant, getAssistantTopics, ASSISTANT_CATEGORY_LABELS,
  type AssistantAnswer, type AssistantTopic,
} from '../../services/assistantService'

// ================================================================================
// TB ASSISTANT — a CONTROLLED, retrieval-based assistant.
//
// It is NOT a generative medical chatbot. It retrieves the best-matching
// curated topic (admin-authored, referencing a real official source) for the
// visitor's question, or a guided fallback. It never diagnoses — emergencies
// and diagnosis requests are routed to a qualified healthcare professional
// (emergencyNote). Answers come from agenda-scoped official TB programme
// material only (Central TB Division / Ni-Kshay).
// ================================================================================

interface Message {
  role: 'user' | 'assistant'
  text: string
  answer?: AssistantAnswer
}

export default function TbAssistantPage() {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [topics, setTopics] = useState<AssistantTopic[]>([])
  const logRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getAssistantTopics().then(setTopics)
  }, [])

  // Welcome message (shown once, before any API call).
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      text: t('pages.assistant.greeting', {
        defaultValue: "Hi, I'm the TB Assistant. I can answer questions about TB symptoms, testing, treatment, becoming a Ni-Kshay Mitra, patient support and finding your nearest TB centre. How can I help?",
      }),
    }])
  }, [t])

  // Auto-scroll to the latest message.
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, loading])

  const send = useCallback(async (text: string) => {
    const query = text.trim()
    if (!query || loading) return
    setInput('')
    setMessages((m) => [...m, { role: 'user', text: query }])
    setLoading(true)
    try {
      const answer = await askAssistant(query)
      setMessages((m) => [...m, { role: 'assistant', text: answer.answer, answer }])
    } catch (e: any) {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: t('pages.assistant.error', { defaultValue: 'Sorry, I could not reach the assistant right now. Please try again in a moment.' }),
      }])
    } finally {
      setLoading(false)
    }
  }, [loading, t])

  const onSubmit = (ev: React.FormEvent) => { ev.preventDefault(); send(input) }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.assistant.seo.title', { defaultValue: 'TB Assistant — controlled answers from official sources' })}
        description={t('pages.assistant.seo.description', { defaultValue: 'A controlled TB assistant that answers common questions from official TB programme sources. It does not diagnose; emergencies are routed to a healthcare professional.' })}
        canonicalPath="/assistant"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.assistant', 'TB Assistant'), path: '/assistant' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.assistant', 'TB Assistant') }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.assistant.hero.eyebrow', { defaultValue: 'TB Assistant' })}
            title={t('pages.assistant.hero.title', { defaultValue: 'Ask the TB Assistant' })}
            description={t('pages.assistant.hero.description', { defaultValue: 'Controlled answers from verified TB programme sources. It answers common questions and routes emergencies to a healthcare professional — it does not diagnose.' })}
          />
        </Container>
      </Section>

      <Section className="bg-white pt-2">
        <Container>
          <Alert tone="warning" title={t('pages.assistant.disclaimer.title', { defaultValue: 'Not a medical chatbot' })}>
            {t('pages.assistant.disclaimer.body', { defaultValue: 'This assistant gives general information from official TB programme sources only. It does not diagnose or give medical advice. For an emergency, call 108 or contact a qualified healthcare professional immediately.' })}
          </Alert>

          <Card className="p-0 mt-6 overflow-hidden flex flex-col" >
            {/* Message log */}
            <div ref={logRef} role="log" aria-live="polite" aria-label={t('pages.assistant.ariaLog', { defaultValue: 'Assistant conversation' })}
              className="h-[460px] overflow-y-auto p-4 sm:p-6 space-y-4 bg-snow-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] sm:max-w-[75%] ${m.role === 'user' ? '' : 'flex gap-3'}`}>
                    {m.role === 'assistant' && (
                      <div className="shrink-0 w-8 h-8 rounded-full bg-[rgb(15_76_129)] text-white flex items-center justify-center" aria-hidden="true">
                        <Bot className="w-5 h-5" />
                      </div>
                    )}
                    <div className={m.role === 'user'
                      ? 'bg-[rgb(15_76_129)] text-white rounded-2xl rounded-br-md px-4 py-3 text-sm'
                      : 'bg-white border border-snow-100 rounded-2xl rounded-bl-md px-4 py-3 text-sm text-snow-800 shadow-sm'}>
                      <p className="whitespace-pre-wrap">{m.text}</p>

                      {/* Assistant answer extras: official source + portal link + emergency note */}
                      {m.answer && (
                        <div className="mt-3 space-y-2">
                          {m.answer.category && (
                            <Badge tone="blue">{t(`pages.assistant.categories.${m.answer.category}`, { defaultValue: ASSISTANT_CATEGORY_LABELS[m.answer.category] ?? m.answer.category })}</Badge>
                          )}
                          {m.answer.emergencyNote && (
                            <div role="alert" className="flex items-start gap-2 p-2.5 rounded-lg bg-[rgb(254_242_242)] border border-[rgb(252_165_165)] text-[rgb(153_27_27)] text-xs">
                              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                              <span>{m.answer.emergencyNote}</span>
                            </div>
                          )}
                          {(m.answer.officialSourceUrl || m.answer.portalLinkPath) && (
                            <div className="flex flex-wrap items-center gap-3 pt-1">
                              {m.answer.officialSourceUrl && (
                                <a href={m.answer.officialSourceUrl} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium text-[rgb(15_76_129)] hover:underline min-h-[44px]">
                                  {m.answer.officialSourceLabel || t('pages.assistant.officialSource', { defaultValue: 'Visit official source' })}
                                  <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                                </a>
                              )}
                              {m.answer.portalLinkPath && (
                                <Link to={m.answer.portalLinkPath}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-[rgb(0_152_186)] hover:underline min-h-[44px]">
                                  {m.answer.portalLinkLabel || t('pages.assistant.openPortal', { defaultValue: 'Open on portal' })}
                                  <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                                </Link>
                              )}
                            </div>
                          )}
                          {m.answer.matched === false && m.answer.suggestions && m.answer.suggestions.length > 0 && (
                            <div className="pt-1">
                              <p className="text-xs text-snow-500 mb-1.5">{t('pages.assistant.tryAsking', { defaultValue: 'Try asking:' })}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {m.answer.suggestions.slice(0, 6).map((s) => (
                                  <button key={s.id} type="button" onClick={() => send(s.title)}
                                    className="px-2.5 py-1 rounded-full bg-snow-100 hover:bg-snow-200 text-xs text-snow-700 min-h-[36px]">
                                    {s.title}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-[rgb(15_76_129)] text-white flex items-center justify-center" aria-hidden="true">
                      <Bot className="w-5 h-5" />
                    </div>
                    <div className="bg-white border border-snow-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-snow-400" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Suggestion chips (initial) */}
            {topics.length > 0 && messages.length <= 1 && (
              <div className="px-4 sm:px-6 py-3 border-t border-snow-100 bg-white">
                <p className="text-xs text-snow-500 mb-2">{t('pages.assistant.tryAsking', { defaultValue: 'Try asking:' })}</p>
                <div className="flex flex-wrap gap-1.5">
                  {topics.map((tp) => (
                    <button key={tp.id} type="button" onClick={() => send(tp.title)}
                      className="px-2.5 py-1 rounded-full bg-snow-100 hover:bg-snow-200 text-xs text-snow-700 min-h-[36px]">
                      {tp.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={onSubmit} className="flex items-end gap-2 p-3 sm:p-4 border-t border-snow-100 bg-white">
              <label htmlFor="assistant-input" className="sr-only">{t('pages.assistant.inputLabel', { defaultValue: 'Ask a question' })}</label>
              <textarea id="assistant-input" rows={1} value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input) } }}
                placeholder={t('pages.assistant.placeholder', { defaultValue: 'Ask about symptoms, testing, treatment, support…' })}
                className="input flex-1 resize-none max-h-32" />
              <button type="submit" disabled={loading || !input.trim()}
                className="btn-primary inline-flex items-center gap-1.5 min-h-[44px] disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="hidden sm:inline">{t('pages.assistant.send', { defaultValue: 'Send' })}</span>
              </button>
            </form>
          </Card>

          <p className="text-xs text-snow-500 mt-4 text-center max-w-2xl mx-auto">
            {t('pages.assistant.footerNote', { defaultValue: 'Answers are curated from official Central TB Division / Ni-Kshay sources. This assistant does not store your questions.' })}
          </p>
        </Container>
      </Section>
    </PortalLayout>
  )
}